import { useQueryClient } from 'react-query'

import { apiPaths, queryKeys } from './config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { handleError, validateStatus } from 'api/kinerja/penilik-pegawai/config'
import { useQueryApi, useMutationApi } from 'utils/api'

import type { AxiosError } from 'axios'
import type { UseQueryOptions } from 'react-query'
import type { CommonResponse } from 'types/api'
import type {
  PerencanaanFilterState,
  PerencanaanPenilik,
  PerencanaanApprovalRequestBody,
  PerencanaanApprovalRevisionRequestBody,
  PerencanaanApprovalApproveRequestBody,
  PerencanaanSummary,
} from 'types/kinerja-tim-penilai-penilik'
import type {
  Perencanaan,
  PerencanaanSubmissionResponse,
} from 'types/kinerja-penilik-pegawai'

export function usePerencanaanPenilikListSummary({
  opts,
}: {
  opts?: UseQueryOptions<PerencanaanSummary>
}) {
  const { period } = usePeriodContext()

  return useQueryApi<PerencanaanSummary>(
    apiPaths.perencanaanListSummary(period.id),
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
      },
      queryKey: [queryKeys.API_GET_LIST_SUMMARY_PERENCANAAN_PENILIK, period.id],
    }
  )
}

export function useAPIGetListPerencanaanPenilik({
  filter = 'ALL',
  opts,
}: {
  filter?: PerencanaanFilterState
  opts?: UseQueryOptions<CommonResponse<PerencanaanPenilik[]>>
}) {
  const { period } = usePeriodContext()

  return useQueryApi<CommonResponse<PerencanaanPenilik[]>>(
    apiPaths.perencanaanList(period.id, filter),
    {
      queryConfig: {
        ...opts,
        useErrorBoundary: false,
      },
      queryKey: [queryKeys.API_GET_LIST_PERENCANAAN_PENILIK, period.id, filter],
    }
  )
}

export function useAPIGetPerencanaanPenilikDetail({
  id,
  ...options
}: UseQueryOptions<Perencanaan> & { id: string }) {
  return useQueryApi<Perencanaan>(apiPaths.perencanaanDetail(id), {
    queryKey: [queryKeys.API_GET_PERENCANAAN_PENILIK_DETAIL, id],
    axiosConfig: {
      validateStatus,
    },
    queryConfig: {
      useErrorBoundary: true,
      ...options,
    },
    errorHandlerFn: handleError,
  })
}

export function useAPISubmitApprovalSend(skpPsId: string) {
  const queryClient = useQueryClient()

  return useMutationApi<
    PerencanaanSubmissionResponse,
    AxiosError,
    PerencanaanApprovalRequestBody
  >(apiPaths.approvalSend, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_PENILIK_DETAIL,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPISubmitApprovalRevision(skpPsId: string) {
  const queryClient = useQueryClient()

  return useMutationApi<
    PerencanaanSubmissionResponse,
    AxiosError,
    PerencanaanApprovalRevisionRequestBody
  >(apiPaths.approvalRevision, {
    axiosConfig: { method: 'POST' },
    mutationConfig: {
      onSettled() {
        queryClient.invalidateQueries([
          queryKeys.API_GET_PERENCANAAN_PENILIK_DETAIL,
          skpPsId,
        ])
      },
    },
  })
}

export function useAPISubmitApprovalApprove() {
  return useMutationApi<
    PerencanaanSubmissionResponse,
    AxiosError,
    PerencanaanApprovalApproveRequestBody
  >(apiPaths.approvalApprove, {
    axiosConfig: { method: 'POST' },
  })
}
